<template>
  <div class="h-full max-w-5xl mx-auto">
    <Form :initial-values="initial_values" :validation-schema="schema" class="space-y-8" @submit="handleSubmit">
      <!-- Error Message -->
      <div v-if="error_message" class="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd" />
          </svg>
          <p class="text-sm font-medium text-red-600 dark:text-red-400">
            {{ error_message }}
          </p>
        </div>
      </div>

      <!-- Date & Time Card -->
      <div class="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 space-y-6">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {{ $t("Date & Time") }}
          </h3>
          <button
            type="submit"
            class="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105">
            {{ $t("Save") }}
          </button>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Date Picker -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              {{ $t("Date") }}
            </label>
            <DatePicker
              v-model="interaction_date"
              :is-dark="$store.state.ui.darkMode"
              :min-date="new Date()"
              class="w-full"
              :popover="{ visibility: 'click' }">
              <template #default="{ inputValue, inputEvents }">
                <div class="relative group">
                  <input
                    :value="inputValue"
                    class="w-full pl-11 pr-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer group-hover:border-blue-400"
                    readonly
                    v-on="inputEvents">
                  <svg
                    class="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </template>
            </DatePicker>
          </div>

          <!-- Start Time -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              {{ $t("Start Time") }}
            </label>
            <div class="relative group">
              <svg
                class="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-500 z-10 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <SelectDropDownMenu
                v-model:current-item="start_time"
                :items="start_time_options.slice(0, start_time_options.length - 1)"
                :label="$t('Start Time')"
                :value-attr="null"
                :placeholder="$t('Select time')"
                input-classes="w-full pl-11 pr-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all group-hover:border-blue-400 appearance-none cursor-pointer" />
            </div>
          </div>

          <!-- End Time -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              {{ $t("End Time") }}
            </label>
            <div v-if="start_time" class="relative group">
              <svg
                class="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-500 z-10 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <SelectDropDownMenu
                v-model:current-item="end_time"
                :items="end_time_options"
                :label="$t('End Time')"
                :value-attr="null"
                :placeholder="$t('Select time')"
                input-classes="w-full pl-11 pr-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all group-hover:border-blue-400 appearance-none cursor-pointer" />
            </div>
            <div
              v-else
              class="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900/50 text-gray-400 dark:text-gray-600 text-sm flex items-center justify-center">
              {{ $t("Select start time first") }}
            </div>
          </div>
        </div>

        <!-- Meeting Link Toggle -->
        <button
          type="button"
          :class="[
            'w-full px-6 py-4 rounded-xl font-semibold transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3 shadow-md',
            generate_meeting
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-green-500/50'
              : 'bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-500 dark:hover:border-blue-500'
          ]"
          @click="generate_meeting = !generate_meeting">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path v-if="generate_meeting"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7" />
            <path
              v-else
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          {{ generate_meeting ? $t("Meeting Link Added") : $t("Add Meeting Link") }}
        </button>
      </div>

      <!-- Meeting Details Card -->
      <div class="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 space-y-6">
        <h3 class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {{ $t("Meeting Details") }}
        </h3>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <!-- Meeting Title -->
          <div class="lg:col-span-2 space-y-2">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              {{ $t("Meeting Title") }}
            </label>
            <TextInput
              name="name"
              :value="event_name"
              type="text"
              :autofocus="true"
              :placeholder="$t('Enter meeting title...')"
              @input="
                $utils.debounce(() => {
                  event_name = $event.target.value
                }, 800)
              " />
          </div>

          <!-- Category -->
          <div class="lg:col-span-1 space-y-2">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              {{ $t("Category") }}
            </label>
            <SelectInput
              :value="meeting_category"
              :is-box-background="true"
              label-attr="label"
              value-attr="value"
              :input-classes="'w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left appearance-none cursor-pointer'"
              :show-label="false"
              name="category"
              :allow-null="false"
              :items="$store.state.data.interaction_types.filter((interaction) => interaction.value == 'meeting')[0].categories"
              @input="
                $utils.debounce(() => {
                  meeting_category = $event.target.value
                }, 800)
              " />
          </div>
        </div>
      </div>

      <!-- Participants Card -->
      <div class="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 space-y-6">
        <h3 class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          {{ $t("Participants") }}
        </h3>

        <div class="relative">
          <!-- Participants List -->
          <div class="flex flex-wrap gap-3">
            <div v-for="(user, i) in participants_list" :key="user.id" class="relative group">
              <div class="transition-transform group-hover:scale-105">
                <Avatar
                  v-if="Record.from(user).info || user.first_name"
                  :hover-effect="true"
                  :first-name="Record.from(user).first_name"
                  :last-name="Record.from(user).last_name"
                  :image="Record.from(user).avatar"
                  :email="Record.from(user).email"
                  :show-tooltip="true"
                  :no-valid-email-effect="true" />
                <div
                  v-else
                  class="px-4 py-2 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 shadow-md">
                  {{ Record.from(user).email }}
                </div>
              </div>
              <button
                v-if="i"
                type="button"
                class="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full text-sm font-bold shadow-lg hover:bg-red-600 hover:scale-110 transition-all transform"
                @click="handleRemoveUser(i)">
                ×
              </button>
            </div>

            <!-- Add Participant Button -->
            <button
              type="button"
              class="w-12 h-12 flex items-center justify-center rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-400 hover:text-blue-500 transition-all transform hover:scale-105"
              @click="add_user_mode = !add_user_mode">
              <Plus class="w-6 h-6" />
            </button>
          </div>

          <!-- Add Participant Dropdown -->
          <div v-if="add_user_mode" class="absolute z-30 w-full max-w-md mt-3 left-0">
            <UserandContactsAutoCompleteInput
              :input-labels="[$t('Team Members'), $t('Contacts')]"
              :label="$t('Add participants')"
              :is-box-background="true"
              :contacts="contacts_list"
              :users="$store.state.data.users.filter((x) => participants_list.filter((s) => s.id == x.id).length == 0)"
              value-attr="full_name"
              @changed="handleChange"
              @focusout="
                () => {
                  $utils.debounce(() => {
                    add_user_mode = false
                  }, 200)
                }
              "
              @reset="add_user_mode = false"
              @chosen="handleAddUser($event)" />
          </div>
        </div>
      </div>

      <!-- Description Card -->
      <div class="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 space-y-4">
        <h3 class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
          </svg>
          {{ $t("Description") }}
        </h3>
        <TextArea
          rows="5"
          input-classes="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
          name="description"
          type="text"
          :placeholder="$t('Add meeting description, agenda, or important notes...')" />
      </div>
    </Form>
  </div>
</template>

<script>
import TextInput from "@/components/shared/TextInput.vue"
import { DatePicker } from "v-calendar"
import Avatar from "@/components/shared/Avatar.vue"

import { Plus } from "lucide-vue-next"
import UserandContactsAutoCompleteInput from "@/components/shared/UserandContactsAutoCompleteInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import { Form } from "vee-validate"
import * as Yup from "yup"

import SelectDropDownMenu from "@/components/shared/SelectDropDownMenu.vue"

import SelectInput from "@/components/shared/SelectInput.vue"

import dataService from "@/services/data.service"
import { Record } from "@/models/record"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    TextInput,
    DatePicker,
    Avatar,
    Plus,
    UserandContactsAutoCompleteInput,
    TextArea,
    Form,

    SelectDropDownMenu,
    SelectInput
  },
  props: {
    selectedDate: {
      type: String,
      required: false
    },
    viewOption: {
      type: String,
      default: "month_view"
    },
    origin: {
      type: String,
      default: "gmail"
    },
    integrationName: {
      type: String,
      default: null
    }
  },
  emits: ["close-modal", "done"],

  setup() {
    const schema = Yup.object().shape({
      name: Yup.string().required()
    })
    const initial_values = { category: null }

    return {
      schema,
      initial_values
    }
  },
  data() {
    return {
      Record,
      generate_meeting: false,
      start_time: null,
      end_time: null,
      start_time_options: [
        "12:00 AM",
        "12:15 AM",
        "12:30 AM",
        "12:45 AM",
        "1:00 AM",
        "1:15 AM",
        "1:30 AM",
        "1:45 AM",
        "2:00 AM",
        "2:15 AM",
        "2:30 AM",
        "2:45 AM",
        "3:00 AM",
        "3:15 AM",
        "3:30 AM",
        "3:45 AM",
        "4:00 AM",
        "4:15 AM",
        "4:30 AM",
        "4:45 AM",
        "5:00 AM",
        "5:15 AM",
        "5:30 AM",
        "5:45 AM",
        "6:00 AM",
        "6:15 AM",
        "6:30 AM",
        "6:45 AM",
        "7:00 AM",
        "7:15 AM",
        "7:30 AM",
        "7:45 AM",
        "8:00 AM",
        "8:15 AM",
        "8:30 AM",
        "8:45 AM",
        "9:00 AM",
        "9:15 AM",
        "9:30 AM",
        "9:45 AM",
        "10:00 AM",
        "10:15 AM",
        "10:30 AM",
        "10:45 AM",
        "11:00 AM",
        "11:15 AM",
        "11:30 AM",
        "11:45 AM",
        "12:00 PM",
        "12:15 PM",
        "12:30 PM",
        "12:45 PM",
        "1:00 PM",
        "1:15 PM",
        "1:30 PM",
        "1:45 PM",
        "2:00 PM",
        "2:15 PM",
        "2:30 PM",
        "2:45 PM",
        "3:00 PM",
        "3:15 PM",
        "3:30 PM",
        "3:45 PM",
        "4:00 PM",
        "4:15 PM",
        "4:30 PM",
        "4:45 PM",
        "5:00 PM",
        "5:15 PM",
        "5:30 PM",
        "5:45 PM",
        "6:00 PM",
        "6:15 PM",
        "6:30 PM",
        "6:45 PM",
        "7:00 PM",
        "7:15 PM",
        "7:30 PM",
        "7:45 PM",
        "8:00 PM",
        "8:15 PM",
        "8:30 PM",
        "8:45 PM",
        "9:00 PM",
        "9:15 PM",
        "9:30 PM",
        "9:45 PM",
        "10:00 PM",
        "10:15 PM",
        "10:30 PM",
        "10:45 PM",
        "11:00 PM",
        "11:15 PM",
        "11:30 PM",
        "11:45 PM",
        "12:00 AM"
      ],
      duration_array: [
        "15 mins",
        "30 mins",
        "45 mins",
        "1 hr",
        "1hr 15min",
        "1.5 hrs",
        "1hr 45min",
        "2 hrs",
        "2hr 15min",
        "2.5 hrs",
        "2hr 45min",
        "3 hrs",
        "3hr 15min",
        "3.5 hrs",
        "3hr 45min",
        "4 hrs",
        "4hr 15min",
        "4.5 hrs",
        "4hr 45min",
        "5 hrs",
        "5hr 15min",
        "5.5 hrs",
        "5hr 45min",
        "6 hrs",
        "6hr 15min",
        "6.5 hrs",
        "6hr 45min",
        "7 hrs",
        "7hr 15min",
        "7.5 hrs",
        "7hr 45min",
        "8 hrs",
        "8hr 15min",
        "8.5 hrs",
        "8hr 45min",
        "9 hrs",
        "9hr 15min",
        "9.5 hrs",
        "9hr 45min",
        "10 hrs",
        "10hr 15min",
        "10.5 hrs",
        "10hr 45min",
        "11 hrs",
        "11hr 15min",
        "11.5  hrs",
        "11hr 45min",
        "12 hrs",
        "12hr 15min",
        "12.5 hrs",
        "12hr 45min",
        "13 hrs",
        "13hr 15min",
        "13.5 hrs",
        "13hr 45min",
        "14 hrs",
        "14hr 15min",
        "14.5 hrs",
        "14hr 45min",
        "15 hrs",
        "15hr 15min",
        "15.5 hrs",
        "15hr 45min",
        "16 hrs",
        "16hr 15min",
        "16.5 hrs",
        "16hr 45min",
        "17 hrs",
        "17hr 15min",
        "17.5 hrs",
        "17hr 45min",
        "18 hrs",
        "18hr 15min",
        "18.5 hrs",
        "18hr 45min",
        "19 hrs",
        "19hr 15min",
        "19.5 hrs",
        "19hr 45min",
        "20 hrs",
        "20hr 15min",
        "20.5 hrs",
        "20hr 45min",
        "21 hrs",
        "21hr 15min",
        "21.5 hrs",
        "21hr 45min",
        "22 hrs",
        "22hr 15min",
        "22.5 hrs",
        "22hr 45min",
        "23 hrs",
        "23hr 15min",
        "23.5 hrs",
        "23hr 45min",
        "24 hrs"
      ],
      end_time_options: [],
      event_name: "",
      participants_list: [],
      add_user_mode: false,

      error_message: false,
      meeting_category: { label: "Support", value: "support" },
      contacts_list: [],
      interaction_date: new Date()
    }
  },
  watch: {
    start_time() {
      let index = this.start_time_options.findIndex((x) => x == this.start_time)
      const end_time_array = this.start_time_options.slice(index + 1, this.start_time_options.length)

      const end_time_with_duration = []
      end_time_array.forEach((time, i) => {
        end_time_with_duration.push(`${time} (${this.duration_array[i]})`)
      })
      this.end_time_options = end_time_with_duration
      this.end_time = null
    },
    selectedDate() {
      this.interaction_date = this.selectedDate
    }
  },
  created() {
    if (this.selectedDate) {
      this.interaction_date = this.selectedDate
    }
    const minute = parseInt(this.$moment(new Date()).format("mm"))
    let hour = parseInt(this.$moment(new Date()).format("hh"))
    let am_pm = this.$moment(new Date()).format("A")
    let rounded_minute = null
    if (minute < 15) {
      rounded_minute = 15
    } else if (minute > 15 && minute < 30) {
      rounded_minute = 30
    } else if (minute > 30 && minute < 45) {
      rounded_minute = 45
    } else {
      rounded_minute = 0
      if (hour == 11) {
        if (am_pm == "AM") {
          am_pm = "PM"
        } else {
          am_pm = "AM"
        }
      }
      if (hour == 12) {
        hour = 1
      } else {
        hour += 1
      }
    }
    this.start_time = `${hour}:${!rounded_minute ? "00" : rounded_minute} ${am_pm}`
    let index = this.start_time_options.findIndex((x) => x == this.start_time)

    setTimeout(() => {
      const end_time_only = this.start_time_options[index + 4]
      const end_time_index = this.end_time_options.findIndex((x) => x.includes(end_time_only))
      this.end_time = this.end_time_options[end_time_index]
    }, 500)
  },

  mounted() {
    this.handleAddUser(this.$store.state.auth.user, true)
  },

  methods: {
    handleChange(string) {
      this.contacts_list = []
      if (string) {
        dataService.getFilteredContacts(string).then((result) => {
          result.data.results.map((user) => {
            this.contacts_list.push(user)
            this.contacts_list = this.$utils.uniqBy(this.contacts_list, "id")
          })
        })
      }
    },
    handleSubmit(values) {
      this.error_message = false
      if (!this.interaction_date) {
        this.error_message = "Meeting date is required"
      } else if (!this.start_time) {
        this.error_message = "start time is required"
      } else if (!this.end_time) {
        this.error_message = "end time is required"
      } else if (!this.participants_list.length) {
        this.error_message = "Participants list is required"
      }

      if (!this.error_message) {
        values["origin"] = this.origin
        values["type"] = "meeting"
        values["category"] = values["category"].value
        values["interaction_date"] = new Date(this.interaction_date)
        values["meta_data"] = {
          generate_meeting_link: this.generate_meeting,
          when: {
            object: "timespan",
            end_time: this.$moment(
              `${this.interaction_date.getFullYear()}-${this.interaction_date.getMonth() + 1}-${this.interaction_date.getDate()} ${
                this.end_time.split(" (")[0]
              }`,
              "YYYY-MM-DD hh:mm A"
            ).unix(),
            start_time: this.$moment(
              `${this.interaction_date.getFullYear()}-${this.interaction_date.getMonth() + 1}-${this.interaction_date.getDate()} ${
                this.start_time
              }`,
              "YYYY-MM-DD hh:mm A"
            ).unix()
          }
        }
        values["contacts"] = []
        values["users"] = []
        const records = []

        this.participants_list.forEach((user) => {
          if (user.type == "contact") {
            if (!records.includes(user.parent)) {
              records.push(user.parent)
            }
            if (user.id) {
              values.contacts.push(user.id)
            } else {
              dataService
                .createRecord({
                  values: { [this.$store.state.integration.metrics_mapping.contact_email_metric]: user.email },
                  object_id: "RANDOM",
                  object_type: this.$utils.getObjectTypeByType("contact").id,
                  origin: "platform-user"
                })
                .then((res) => {
                  values.contacts.push(res.data.id)
                })
            }
          } else {
            values.users.push(user.id)
          }
        })
        if (records.length) {
          values["record"] = records[0]
        } else {
          values["record"] = null
        }
        values["meta_data"]["send_event"] = true

        this.$store
          .dispatch("data/bulkInteractionCreate", {
            interaction: {
              interaction: {
                ...values,
                tags: [],
                tasks: [],
                start: this.start_time,
                end: this.end_time,
                object_type: null,
                is_all_records: false
              },
              records: values["record"] ? [values["record"]] : [],
              excluded_records: [],
              is_all_records: false,
              object_type: null
            }
          })
          .then(() => {
            this.$emitter.emit(ALL_EMITTERS.NEW_MEETING_CREATED, this.interaction_date)
            this.$emit("done")
            this.$emit("close-modal")
          })
      }
    },
    handleAddUser(e, dont_generate) {
      this.participants_list.push(e)
      this.add_user_mode = false
      if (!dont_generate) {
        this.generate_meeting = true
      }
    },
    handleRemoveUser(i) {
      this.participants_list.splice(i, 1)
      if (this.participants_list.length == 1) {
        this.generate_meeting = false
      }
    }
  }
}
</script>
